import { Component, OnInit, OnDestroy } from '@angular/core';
import { JeevesSalesCartListComponent, JeevesSalesCartListRow } from 'gung-standard-jeeves';
import {
  ProductService,
  CartService,
  PriceService,
  AvailabilityService,
  SelectedCustomerService,
  AuthService,
  GungFlowService,
  Product,
  CartRowPrice,
  Availability,
  CartRow,
  Customer,
  PriceConfigService
} from 'gung-standard';
import { DateUtilService } from 'gung-common';
import { Observable, Subject, first, forkJoin, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CarLinerCheckoutAddRowModalComponent } from '../car-liner-checkout-add-row-modal/car-liner-checkout-add-row-modal.component';
import { format } from 'date-fns';
import { CarLinerModalService } from '../../services/car-liner-modal.service';
import { CarLinerBrochuresHelper, CarLinerBrochuresHelperService } from '../../services/car-liner-brochures-helper.service';

@Component({
    selector: 'car-liner-sales-cart-list',
    templateUrl: './car-liner-sales-cart-list.component.html',
    styleUrls: ['./car-liner-sales-cart-list.component.scss'],
    standalone: false
})
export class CarLinerSalesCartListComponent extends JeevesSalesCartListComponent implements OnInit, OnDestroy {
  isUserDistributor: boolean;
  isDistributor: boolean;
  setExtraPriceOnInit = {};
  setExtraDiscountOnInit = {};
  public hidePrice: boolean;
  unsubscribe: Subject<boolean> = new Subject<boolean>();
  private cachedDataProducts: { [id: string]: Product } = {};
  public brochures: {[id: string]: CarLinerBrochuresHelper} = {};

  public hasRoleOrder: boolean = false
  
  constructor(
    protected productService: ProductService,
    protected cartService: CartService,
    protected priceService: PriceService,
    protected availabilityService: AvailabilityService,
    protected selectedCustomerService: SelectedCustomerService,
    protected dateUtilService: DateUtilService,
    protected authService: AuthService,
    protected modalService: NgbModal,
    protected flowService: GungFlowService,
    protected priceConfigService: PriceConfigService,
    protected carLinerModalService: CarLinerModalService,
    protected carLinerBrochuresHelperService: CarLinerBrochuresHelperService
  ) {
    super(
      productService,
      cartService,
      priceService,
      availabilityService,
      selectedCustomerService,
      dateUtilService,
      flowService
    );
    this.priceConfigService.hidePrice.pipe(takeUntil(this.unsubscribe)).subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isUserDistributor = roles.indexOf('USER') > -1 && roles.indexOf('DISTRIBUTOR') > -1;
        this.isDistributor = roles.indexOf('DISTRIBUTOR') > -1;
        this.hasRoleOrder = roles.indexOf('ORDER') > -1;
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsubscribe.next(false);
    this.unsubscribe.complete();
  }

  public updateDiscountPercentage(row: JeevesSalesCartListRow, value: any, totalDiscount?: boolean) {
    value = Math.abs(value);
    const oldVal = row.discountPercent;

    if (oldVal === value && this.setExtraDiscountOnInit[row.productId]) {
      return;
    }
    // eslint-disable-next-line eqeqeq
    if (!value && value != 0) {
      this.cartService.removeExtraField(row.productId, 'orp.rabatt1', row.targetStockId, row.productPartialId);
      if (totalDiscount) {
        this.cartService.removeExtraField(row.productId, 'orp.rabatt2', row.targetStockId, row.productPartialId);
        this.cartService.removeExtraField(row.productId, 'orp.rabatt3', row.targetStockId, row.productPartialId);
        this.cartService.removeExtraField(row.productId, 'orp.kundrabatt', row.targetStockId, row.productPartialId);
        this.cartService.removeExtraField(row.productId, 'orp.volymrabatt', row.targetStockId, row.productPartialId);
      }
      return;
    }

    const extra: any = {
      orp: {
        rabatt1: value
      }
    };

    if (totalDiscount) {
      extra.orp.rabatt2 = '0';
      extra.orp.rabatt3 = '0';
      extra.orp.kundrabatt = '0';
      extra.orp.volymrabatt = '0';
    }

    this.cartService.setExtra(row.productId, extra, row.targetStockId, row.productPartialId);
  }

  addNewOrderLine(): void {
    this.modalService.open(CarLinerCheckoutAddRowModalComponent, { size: 'lg', backdrop: 'static' });
  }

  updateData() {
    // Product 46607-03 is a virtual custom order line (freight/fee) added by sales/admin via the "+" button
    // (CarLinerCheckoutAddRowModalComponent). It has no entry in the product catalog, so
    // productService.getProductsByIds() never returns it. The parent's updateData() builds cachedData
    // by mapping fetched products back to their IDs — if the product is missing, it crashes with
    // "Cannot read properties of undefined (reading 'id')". Pre-populating cachedData here prevents
    // 46607-03 from being included in the fetch request entirely, avoiding the crash.
    for (const cartRow of this.data || []) {
      if (!this.cachedData[cartRow.productId] && cartRow.productId === '46607-03') {
        this.cachedData[cartRow.productId] = {
          product: {
            id: cartRow.productId,
            name: cartRow.extra?.orp?.artbeskr || cartRow.productId,
            extra: {}
          } as Product,
          price: null,
          availability: null
        };
      }
    }
    super.updateData();
  }

  public updateOverridePrice(row: JeevesSalesCartListRow, event: any, userChangePrice = false) {
    if (Number(event.target.value) <= 0) {
      event.target.value = '0.00';
    }
    let value = event.target.value;
    if(isNaN(value)){
      value = this.convertStringToNumber(value)
    }
    const oldVal = row.overridePrice || row.originalPrice;

    if (oldVal === value && this.setExtraPriceOnInit[row.productId]) {
      return;
    }

    if (!value || isNaN(value)) {
      this.cartService.removeExtraField(row.productId, 'orp.vb_pris', row.targetStockId, row.productPartialId);
      return;
    }

    this.cartService.setExtra(
      row.productId,
      {
        orp: {
          vb_pris: value
        },
        _userChangePrice: userChangePrice
      },
      row.targetStockId,
      row.productPartialId
    );
    
  }

  convertStringToNumber(str) {
    // Remove any spaces and replace the comma with a dot
    const cleanedStr = str.replace(/\s/g, '').replace(',', '.');
  
    // Parse the cleaned string as a float
    const number = parseFloat(cleanedStr);
  
    // Return the parsed number
    return number;
  }

  public mapData(
    products: Product[],
    prices: CartRowPrice[],
    availabilities: Availability[],
    cartRows: CartRow[],
    customer: Customer
  ): JeevesSalesCartListRow[] {
    for (const cartRow of cartRows) {
      
    }
    return cartRows.map(cr => {
      const product = products.find(p => p.id === cr.productId) || ({
        id: cr.productId,
        name: cr.extra?.orp?.artbeskr || cr.productId,
        extra: {}
      } as Product);
      const price = prices.find(
        p => p.productId === cr.productId && (!p.productPartialId || p.productPartialId === cr.productPartialId)
      ) || ({
        customerGrossPrice: { value: cr.extra?.orp?.vb_pris || 0 },
        customerNetPrice: { currencyCode: '' },
        cartRowDiscountPercent: 0,
        cartRowTotalPriceInclRowDiscount: { value: 0 }
      } as CartRowPrice);
      const av = this.currentFlowRequireAvailability ? availabilities.find(a => a.productId === cr.productId) : null;
      const minDateJs: Date = this.getMinDate(av);
      const toReturn: JeevesSalesCartListRow = {
        productId: cr.productId,
        productPartialId: cr.productPartialId,
        name: product.name,
        quantity: cr.qty,
        originalPrice: price.customerGrossPrice.value,
        currency: price.customerNetPrice.currencyCode,
        minDate: new NgbDate(minDateJs.getFullYear(), minDateJs.getMonth() + 1, minDateJs.getDate()),
        targetStockId: cr.targetStockId,
        cartRowPriceElement: price,
        deliveryMethod: customer.extra.kus.levsattkod,
        cartRow: cr,
        currentAvailability: this.currentFlowRequireAvailability ? av.currentAvailability : 0,
        product,
        price
      };
      if(!this.setExtraPriceOnInit[toReturn.productId]){
        if(!cr.extra?.orp?.vb_pris){
          this.updateOverridePrice(toReturn, {target:{value:toReturn.originalPrice}});
          this.setExtraPriceOnInit[toReturn.productId] = true;
        }
        if(cr.extra?.orp?.vb_pris && !cr.extra?._userChangePrice){
          this.cartService.removeExtraField(cr.productId, 'orp.vb_pris', cr.targetStockId, cr.productPartialId);
        }
        
      }
      if(!this.setExtraDiscountOnInit[toReturn.productId]){
        if(!cr.extra?.orp?.rabatt1){
          this.updateDiscountPercentage(toReturn, toReturn.cartRowPriceElement.cartRowDiscountPercent)
        }
        this.setExtraDiscountOnInit[toReturn.productId] = true;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.rabatt1) {
        toReturn.discountPercent = cr.extra.orp.rabatt1;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.rabatt2) {
        toReturn.discountPercent2 = cr.extra.orp.rabatt2;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.vb_pris) {
        toReturn.overridePrice = cr.extra.orp.vb_pris;
      }else{
        toReturn.overridePrice = toReturn.originalPrice;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.artbeskr) {
        toReturn.description = cr.extra.orp.artbeskr;
      }
      if (this.getDeliveryDateFromRow(cr)) {
        const setDate = new Date(this.getDeliveryDateFromRow(cr));
        toReturn.deliveryDate = format(setDate, 'yyyy-MM-dd');
      }
      if (products.find(p => p.id === cr.productId)) {
        this.brochures[product.id] = this.carLinerBrochuresHelperService.getBrochuresById(product.id);
      }
      return toReturn;
    });
  }

  addBrochures(row: JeevesSalesCartListRow) {
    this.carLinerModalService.openBrochuresAddModal(this.brochures[row.product.id].products.map(b => b.id), row.product, row.quantity).then(
      result => {
        if (!result) { // Not add brochures
          this.carLinerBrochuresHelperService.setBrochuresDecision(row.cartRow.productId, !result);
        }
      },
      reason => { }
    );
  }

  removeAllDiscounts(){
    let bulkExtraOps = [];
    for (const data of this.mappedData) {
      bulkExtraOps.push({
        productId: data.cartRow.productId,
        extra: {
          ...data.cartRow.extra,
          orp: {
            ...data.cartRow.extra.orp,
            rabatt1: 0
          }
        },
        targetStockId: data.cartRow.targetStockId,
        productPartialId: data.cartRow.productPartialId
      });
    }
    this.cartService.bulkSetExtra(bulkExtraOps);
  }

  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    const allowedKeyCodes = [8, 46, 9, 27, 13]; // key codes for Backspace, Delete, Tab, Escape, Enter
    const input = event.key;
  
    if (
      allowedKeys.indexOf(input) !== -1 ||
      allowedKeyCodes.indexOf(event.keyCode) !== -1
    ) {
      // Allow the allowed keys to be entered
      return;
    }
  
    // Prevent the input if it's not a digit or decimal point
    if (!/[\d.]/.test(input)) {
      event.preventDefault();
    }
  }
}
